<template>
  <div
    class="channel-item d-flex align-center"
    v-bind:class="{ 'channel-item-active': active, 'channel-item-passive': !active }"
  >
    <div class="d-flex channel-item-body">
      <v-badge dot color="success" offset-x="18" offset-y="9" :value="singleOtherUser && singleOtherUser.online">
        <Avatar v-if="singleOtherParticipant" :userId="singleOtherParticipant.uid" size="36" class="mr-3" />
      </v-badge>
      <div class="d-inline-flex flex-column channel-item-body-con justify-space-between">
        <div v-if="singleOtherParticipant" class="channel-item-body-title">
          {{ singleOtherParticipant.displayName }}
        </div>
        <div v-else class="channel-item-body-title">
          {{ channel.friendlyName }}
        </div>
        <div v-if="chiefComplaint" class="channel-item-body-chief text-justify">
          {{ chiefComplaint }}
        </div>
        <div v-if="messagePreview" class="channel-item-body-preview text-justify">
          {{ messagePreview.body }}
        </div>
      </div>
    </div>
    <div class="d-inline-flex fill-height flex-column align-end justify-start channel-item-info justify-space-between">
      <div class="channel-item-info-time">
        {{ lastMessageDate }}
      </div>
      <div class="channel-item-info-count" v-bind:class="{ hidden: !channel.unconsumedMessagesCount }">
        <span v-if="channel.unconsumedMessagesCount">
          {{ channel.unconsumedMessagesCount }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";

import Avatar from "@/components/Avatar/Index";

export default Vue.extend({
  name: "ChannelItem",
  props: ["channel", "active"],
  components: {
    Avatar,
  },
  data: () => ({
    messagePreview: null,
    otherMembers: [],
    otherUsers: [],
  }),
  computed: {
    ...mapGetters("chat", ["memberId", "participants"]),
    lastMessageDate() {
      if (!this.channel || !this.channel.lastMessage || !this.channel.lastMessage.dateCreated) {
        return;
      }
      const date = DateTime.fromJSDate(this.channel.lastMessage.dateCreated);
      if (date < DateTime.local().startOf("day")) {
        return date.toFormat("MMM d");
      } else {
        return date.toFormat("t");
      }
    },
    singleOtherParticipant() {
      if (this.otherMembers.length === 1) {
        return this.participants[this.otherMembers[0].identity];
      }
      return null;
    },
    singleOtherUser() {
      if (this.otherUsers.length === 1) {
        return this.otherUsers[0];
      }
      return null;
    },
    chiefComplaint() {
      const val = this.channel.attributes?.chiefComplaint;
      return this.$t("general.chiefComplaints")[val] || val || undefined;
    },
  },
  methods: {
    ...mapActions("chat", ["getMember"]),
    async getMembers() {
      if (!this.channel) {
        return;
      }
      const members = await this.channel.getMembers();
      this.otherMembers = members.filter((member) => member.identity !== this.memberId);
      await Promise.all(this.otherMembers.map((member) => this.getMember(member.identity)));
      this.otherUsers = await Promise.all(this.otherMembers.map((member) => member.getUser()));
    },
    async getMessagePreview() {
      if (!this.channel) {
        return;
      }
      const messagesWrapper = await this.channel.getMessages(1);
      const [messagePreview] = messagesWrapper.items;
      this.messagePreview = messagePreview;
      Vue.set(this.channel, "unconsumedMessagesCount", await this.channel.getUnconsumedMessagesCount());
    },
    async messageAdded(message) {
      this.messagePreview = message;
      if (message.author === this.memberId) {
        return;
      }
      if (this.active) {
        return;
      }
      Vue.set(this.channel, "unconsumedMessagesCount", await this.channel.getUnconsumedMessagesCount());
    },
  },
  async mounted() {
    this.channel.on("messageAdded", this.messageAdded);
    this.channel.on("memberJoined", this.getMembers);
    this.channel.on("memberLeft", this.getMembers);
    this.channel.on("memberUpdated", this.getMembers);
    await Promise.all([this.getMembers(), this.getMessagePreview()]);
  },
});
</script>

<style scoped lang="scss">
.channel-item {
  border-bottom: 1px solid #ebebeb;
  box-sizing: content-box;
  padding-bottom: 17px;
  padding-top: 16px;
  height: 44px;
  &-active {
    background: #d9edff;
    box-shadow: 1.5px 0 0 0 #2196f3 inset;
  }
  &-passive:hover {
    background: #f7fcff;
    cursor: pointer;
  }
  &-info {
    width: 100px;
    padding-right: 18px;
    &-time {
      font-family: Helvetica Neue;
      font-style: normal;
      font-weight: normal;
      font-size: 11.5px;
      line-height: 20px;
      color: #6c757d;
    }
    &-count {
      width: 23px;
      height: 23px;
      border-radius: 50%;
      background: #0073ff;
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 23px;
      letter-spacing: 0.25px;
      color: #ffffff;
      text-align: center;
    }
  }
  &-body {
    margin-left: 18px;
    box-sizing: border-box;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &-con {
      width: calc(100% - (36px + 10px));
    }
    &-title {
      font-style: normal;
      font-weight: bold;
      font-size: 13.5px;
      line-height: 20px;
      letter-spacing: 0.25px;
      color: #1e85d9;

      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      width: 100%;
    }
    &-preview {
      font-weight: 500;
      font-size: 12px;
      line-height: 15px;
      text-align: center;
      color: #4d5f73;

      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      width: 100%;
      font-stretch: condensed;
    }
    &-chief {
      font-weight: bold;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.25px;
      color: $primaryblack2;
      margin-bottom: 2px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
}
.hidden {
  visibility: hidden;
}
</style>
